import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { AdminCourseNotFound } from "@/features/admin/components/AdminCourseNotFound";
import { DraftVersionPage } from "@/features/admin/edit-course/DraftVersionPage";
import { getDraftState } from "@/features/admin/edit-course/contentAndVersions";
import { isUnreleasedDraft } from "@/features/admin/edit-course/editCourseUtils";
import { useCourseIngestionUpdates } from "@/features/admin/edit-course/useCourseIngestionUpdates";
import { adminGetCourse } from "@/services/admin/courses";
import type { CourseDetailDto } from "@/features/admin/types";

export const Route = createFileRoute("/admin/courses/$courseId_/versions/$versionId/draft")({
  component: DraftVersionRoute,
  remountDeps: ({ params }) => `${params.courseId}/${params.versionId}`,
});

/**
 * Check the draft before publishing, once its file has been checked. Any other version, or a draft
 * whose file is being checked or has a problem, goes back to Content and versions.
 */
function DraftVersionRoute() {
  const { courseId, versionId } = Route.useParams();

  // The same query as the edit course page, so going back and forth shares one copy of the course.
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "courses", courseId],
    queryFn: ({ signal }) => adminGetCourse(courseId, signal),
    retry: (failureCount, error) => {
      if (isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  }) as { data: CourseDetailDto | undefined; isLoading: boolean; error: Error | null };

  // A file replaced elsewhere is checked again; the modules and settings change once it finishes.
  useCourseIngestionUpdates(courseId, data);

  // A failed refetch keeps the page, and any unsaved edits, on the copy of the course it has.
  if (error && !data) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return (
        <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
          <AdminCourseNotFound />
        </ProtectedRoute>
      );
    }
    return (
      <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
        <AdminPageLayout mainClassName="bg-secondary_alt">
          <div className="bg-secondary_alt flex min-h-[60vh] items-center justify-center">
            <p className="text-sm text-red-600">Unable to load course details.</p>
          </div>
        </AdminPageLayout>
      </ProtectedRoute>
    );
  }

  if (isLoading || !data) {
    return (
      <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
        <AdminPageLayout mainClassName="bg-secondary_alt">
          <div className="bg-secondary_alt flex min-h-[60vh] items-center justify-center">
            <p className="text-tertiary text-sm">Loading course details…</p>
          </div>
        </AdminPageLayout>
      </ProtectedRoute>
    );
  }

  const version = data.versions?.find((item) => item.id === versionId);
  if (!version?.id || !isUnreleasedDraft(version) || getDraftState(version) !== "ready") {
    return (
      <Navigate
        to="/admin/courses/$courseId"
        params={{ courseId }}
        search={{ tab: "content" }}
        replace
      />
    );
  }

  return (
    <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
      <AdminPageLayout mainClassName="bg-secondary_alt">
        <DraftVersionPage
          courseId={courseId}
          course={data}
          version={{ ...version, id: version.id }}
        />
      </AdminPageLayout>
    </ProtectedRoute>
  );
}
